/**
 * Chart Card Component
 * Standardized wrapper for Recharts visualizations with consistent styling
 */

import React from 'react';
import { Card, CardContent, Box, Typography, IconButton, Tooltip } from '@mui/material';
import { Icon } from '@iconify/react';
import { getPalette, useThemeMode } from '../../context/ThemeContext';
import { makeRecipes } from '../../theme/recipes';
import UpdatedStamp from './UpdatedStamp';

// Theme-aware chart styling. Now a shim over the shared F recipes so every
// consumer gets the SAME chart language as the public pages: navy tooltip,
// mono tabular axis ticks, hairline dashed grid. (The old implementation was
// a second, contradictory theme - white tooltips, proportional ticks.)
export function getChartStyles(isDark) {
  const chart = makeRecipes(getPalette(isDark ? 'dark' : 'light')).chart;
  return {
    tooltip: chart.tooltip,
    grid: chart.gridStyle,
    axis: chart.axisStyle,
  };
}

// Light-mode fallbacks - kept so any un-updated call site still renders correctly.
const _lightStyles = getChartStyles(false);
export const chartTooltipStyle = _lightStyles.tooltip;
export const chartGridStyle = _lightStyles.grid;
export const chartAxisStyle = _lightStyles.axis;

// Chart color palette - "Data Product" v3 ramp: green primary, navy anchor,
// then muted supports. Order is tuned so the first entries pick brand primaries.
export const chartColors = [
  '#1FA254', // Deep signal green - primary series
  '#0F2744', // Deep Navy - anchor series
  '#1E4D80', // Institutional Blue
  '#B45309', // Amber - caution / highlight
  '#DA3B3B', // Refined red - declines
  '#0E7490', // Muted teal - complementary
  '#374151', // Slate
  '#9AA3AF', // Muted grey
];

// Custom legend with colored dots (palette-aware labels)
export function ChartLegend({ items }) {
  const { palette } = useThemeMode();
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1 }}>
      {items.map((item) => (
        <Box key={item.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: item.color, flexShrink: 0 }} />
          <Typography variant="body2" sx={{ color: palette.textSecondary, fontSize: '0.75rem', fontWeight: 500 }}>
            {item.label}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

function ChartCard({
  title,
  titleSx,         // optional sx override for the title Typography
  subtitle,
  actions,
  children,
  height = 300,
  legendItems,
  showFullscreen = false,
  onFullscreen,
  topBar,
  stamp,           // ISO date of the newest datum -> renders "Updated · date"
  sx: cardSx,
}) {
  return (
    <Card
      sx={{
        height: '100%',
        ...cardSx,
      }}
    >
      <CardContent sx={{ p: 2.5 }}>
        {/* Optional top bar (e.g. market mode toggle) - sits above the title
            with a divider beneath it so it visually owns the whole card. */}
        {topBar && (
          <Box sx={{ mb: 2, pb: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
            {topBar}
          </Box>
        )}

        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            mb: 2,
          }}
        >
          <Box>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 600, color: 'text.primary', lineHeight: 1.3, ...titleSx }}
            >
              {title}
            </Typography>
            {subtitle && (
              <Typography
                variant="body2"
                sx={{ color: 'text.secondary', mt: 0.25, fontSize: '0.78rem' }}
              >
                {subtitle}
              </Typography>
            )}
            {stamp && <UpdatedStamp date={stamp} sx={{ mt: 0.5 }} />}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {actions}
            {showFullscreen && onFullscreen && (
              <Tooltip title="Fullscreen">
                <IconButton size="small" aria-label="View chart fullscreen" onClick={onFullscreen} sx={{ color: 'text.secondary' }}>
                  <Icon icon="solar:full-screen-outline" width={18} height={18} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>

        {/* Chart content */}
        <Box sx={{ height, width: '100%' }}>
          {children}
        </Box>

        {/* Legend */}
        {legendItems && legendItems.length > 0 && (
          <ChartLegend items={legendItems} />
        )}
      </CardContent>
    </Card>
  );
}

export default ChartCard;
